'use client'

import Link from 'next/link'
import { useLocale, useTranslations } from 'next-intl'
import { useRouter } from '@bprogress/next/app'
import { useQueryClient } from '@tanstack/react-query'
import { useState, useEffect, useRef } from 'react'
import { toast } from 'sonner'
import {
  ArrowLeftIcon,
  TrashIcon,
  DownloadIcon,
  BriefcaseIcon,
  UserIcon,
  MailIcon,
  PhoneIcon,
  CalendarIcon,
  BookmarkCheckIcon,
  MapPinIcon,
  CarIcon,
  HeartPulseIcon,
  PlaneIcon,
  GraduationCapIcon,
  LanguagesIcon,
  BuildingIcon,
  WrenchIcon,
  AwardIcon,
  UsersIcon,
  CopyIcon
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Separator } from '@/components/ui/separator'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import {
  ContentError,
  ContentDetailFiles,
  ContentDetailPicture,
  DeleteConfirmationDialog,
  UserAvatar
} from '@/components/partials'
import { useJobApplication, useDeleteJobApplication, useDownloadJobApplicationPdf, useCopyToClipboard } from '@/hooks'
import { useMeStore } from '@/store'
import { formatDateTime, formatDate } from '@/lib/utils'
import { hasPermission } from '@/lib/permissions'
import { DashboardResponseType } from '@/types/dashboard'
import {
  EducationEntryType,
  ForeignLanguageEntryType,
  WorkExperienceEntryType,
  SkillEntryType,
  CertificationEntryType,
  ReferenceEntryType
} from '@/types/job-application'

type PropsType = {
  id: string
}

const JobApplicationShowSkeleton = () => (
  <div className="space-y-6">
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-4">
        <Skeleton className="h-9 w-16" />
        <div className="space-y-2">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-4 w-64" />
        </div>
      </div>
      <div className="flex flex-row gap-3">
        <Skeleton className="h-9 w-10 md:w-40" />
        <Skeleton className="h-9 w-10 md:w-20" />
      </div>
    </div>
    <Card>
      <CardHeader>
        <Skeleton className="h-6 w-48" />
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="space-y-2">
              <Skeleton className="h-4 w-20" />
              <Skeleton className="h-6 w-32" />
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  </div>
)

const EducationTable = ({
  items,
  t,
  locale
}: {
  items: EducationEntryType[]
  t: (key: string) => string
  locale: string
}) => (
  <div className="space-y-2">
    <p className="text-muted-foreground flex items-center gap-1 text-sm font-medium">
      <GraduationCapIcon className="h-3.5 w-3.5" />
      {t('education')}
    </p>
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t('level')}</TableHead>
            <TableHead>{t('school_name')}</TableHead>
            <TableHead>{t('department')}</TableHead>
            <TableHead>{t('period')}</TableHead>
            <TableHead>{t('graduation_status')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.map((item, i) => (
            <TableRow key={i}>
              <TableCell>{t(item.level)}</TableCell>
              <TableCell className="font-medium">{item.schoolName}</TableCell>
              <TableCell>{item.department ?? '—'}</TableCell>
              <TableCell>
                {item.endDate
                  ? `${formatDate(item.startDate, locale)} – ${formatDate(item.endDate, locale)}`
                  : `${formatDate(item.startDate, locale)} – …`}
              </TableCell>
              <TableCell>{t(item.graduationStatus)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  </div>
)

const ForeignLanguagesTable = ({ items, t }: { items: ForeignLanguageEntryType[]; t: (key: string) => string }) => (
  <div className="space-y-2">
    <p className="text-muted-foreground flex items-center gap-1 text-sm font-medium">
      <LanguagesIcon className="h-3.5 w-3.5" />
      {t('foreign_languages')}
    </p>
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t('language')}</TableHead>
            <TableHead>{t('listening')}</TableHead>
            <TableHead>{t('speaking')}</TableHead>
            <TableHead>{t('reading')}</TableHead>
            <TableHead>{t('writing')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.map((item, i) => (
            <TableRow key={i}>
              <TableCell className="font-medium">{item.language}</TableCell>
              <TableCell>{t(item.listening)}</TableCell>
              <TableCell>{t(item.speaking)}</TableCell>
              <TableCell>{t(item.reading)}</TableCell>
              <TableCell>{t(item.writing)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  </div>
)

const WorkExperienceTable = ({
  items,
  t,
  locale
}: {
  items: WorkExperienceEntryType[]
  t: (key: string) => string
  locale: string
}) => (
  <div className="space-y-2">
    <p className="text-muted-foreground flex items-center gap-1 text-sm font-medium">
      <BuildingIcon className="h-3.5 w-3.5" />
      {t('work_experience')}
    </p>
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t('company')}</TableHead>
            <TableHead>{t('position')}</TableHead>
            <TableHead>{t('period')}</TableHead>
            <TableHead>{t('description')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.map((item, i) => (
            <TableRow key={i}>
              <TableCell className="font-medium">{item.companyName}</TableCell>
              <TableCell>{item.position}</TableCell>
              <TableCell>
                {item.endDate
                  ? `${formatDate(item.startDate, locale)} – ${formatDate(item.endDate, locale)}`
                  : `${formatDate(item.startDate, locale)} – …`}
              </TableCell>
              <TableCell className="max-w-xs truncate">{item.description ?? '—'}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  </div>
)

const SkillsTable = ({ items, t }: { items: SkillEntryType[]; t: (key: string) => string }) => (
  <div className="space-y-2">
    <p className="text-muted-foreground flex items-center gap-1 text-sm font-medium">
      <WrenchIcon className="h-3.5 w-3.5" />
      {t('skills')}
    </p>
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t('skill_name')}</TableHead>
            <TableHead>{t('level')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.map((item, i) => (
            <TableRow key={i}>
              <TableCell className="font-medium">{item.skillName}</TableCell>
              <TableCell>{t(item.level)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  </div>
)

const CertificationsTable = ({
  items,
  t,
  locale
}: {
  items: CertificationEntryType[]
  t: (key: string) => string
  locale: string
}) => (
  <div className="space-y-2">
    <p className="text-muted-foreground flex items-center gap-1 text-sm font-medium">
      <AwardIcon className="h-3.5 w-3.5" />
      {t('certifications')}
    </p>
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t('certification_name')}</TableHead>
            <TableHead>{t('issuing_organization')}</TableHead>
            <TableHead>{t('certification_date')}</TableHead>
            <TableHead>{t('validity_period')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.map((item, i) => (
            <TableRow key={i}>
              <TableCell className="font-medium">{item.certificationName}</TableCell>
              <TableCell>{item.issuingOrganization}</TableCell>
              <TableCell>{item.certificationDate ? formatDate(item.certificationDate, locale) : '—'}</TableCell>
              <TableCell>{item.validityPeriod ?? '—'}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  </div>
)

const ReferencesTable = ({ items, t }: { items: ReferenceEntryType[]; t: (key: string) => string }) => (
  <div className="space-y-2">
    <p className="text-muted-foreground flex items-center gap-1 text-sm font-medium">
      <UsersIcon className="h-3.5 w-3.5" />
      {t('references')}
    </p>
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t('full_name')}</TableHead>
            <TableHead>{t('position')}</TableHead>
            <TableHead>{t('phone')}</TableHead>
            <TableHead>{t('email')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.map((item, i) => (
            <TableRow key={i}>
              <TableCell className="font-medium">{item.name}</TableCell>
              <TableCell>{item.position ?? '—'}</TableCell>
              <TableCell>{item.phone ?? '—'}</TableCell>
              <TableCell>{item.email ?? '—'}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  </div>
)

const Detail = ({ id }: PropsType) => {
  const t = useTranslations()
  const router = useRouter()
  const queryClient = useQueryClient()
  const { copyToClipboard } = useCopyToClipboard()
  const locale = useLocale()
  const [deleteId, setDeleteId] = useState<string | null>(null)

  const { data: application, isLoading, error } = useJobApplication(id)
  const { mutate: deleteMutate, isPending: isDeleting } = useDeleteJobApplication()
  const { mutate: downloadPdf, isPending: isDownloading } = useDownloadJobApplicationPdf()
  const { meState } = useMeStore()
  const canDelete = hasPermission(meState.response, 'JOB_APPLICATIONS', 'DELETE')
  const canShow = hasPermission(meState.response, 'JOB_APPLICATIONS', 'SHOW')

  const decrementedRef = useRef(false)

  useEffect(() => {
    if (application?.readAt && !decrementedRef.current) {
      decrementedRef.current = true

      queryClient.setQueryData<DashboardResponseType>(['dashboard'], old => {
        if (!old?.stats) {
          return old
        }
        return {
          ...old,
          stats: {
            ...old.stats,
            unreadJobApplications: Math.max(0, (old.stats.unreadJobApplications ?? 0) - 1)
          }
        }
      })

      queryClient.setQueriesData<{ items: any[] }>({ queryKey: ['job-applications'] }, old => {
        if (!old?.items) {
          return old
        }
        return {
          ...old,
          items: old.items.map((item: any) => (item.id === id ? { ...item, readAt: application.readAt } : item))
        }
      })
    }
  }, [application?.readAt, queryClient, id])

  if (isLoading) {
    return <JobApplicationShowSkeleton />
  }
  if (error || !application) {
    return <ContentError error={error} listUrl="/user-interactions/job-applications" />
  }

  const confirmDelete = () => {
    deleteMutate(id, {
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: ['job-applications'] })
        toast.success(t('deleted_successfully'))
        router.push('/user-interactions/job-applications')
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.message ?? t('error_deleting'))
        setDeleteId(null)
      }
    })
  }

  return (
    <>
      <div className="space-y-6">
        {/* Header */}
        <div className="grid grid-cols-1 items-center justify-between gap-2 md:grid-cols-2">
          <div className="flex items-center gap-4">
            <Button variant="outline" size="sm" asChild>
              <Link href="/user-interactions/job-applications">
                <ArrowLeftIcon className="h-4 w-4" />
                <span className="hidden lg:inline">{t('back')}</span>
              </Link>
            </Button>
            <div>
              <h1 className="flex items-center gap-2 text-2xl font-bold">
                <BriefcaseIcon className="h-6 w-6" />
                {`${application.firstname} ${application.lastname}`}
              </h1>
              <div className="flex items-center gap-2">
                <p className="text-muted-foreground font-mono text-sm">{application.id}</p>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => copyToClipboard(application.id, t('copied'))}
                  className="h-6 w-6 p-0">
                  <CopyIcon className="h-3 w-3" />
                </Button>
              </div>
            </div>
          </div>
          <div className="flex flex-row items-center justify-end gap-2">
            {canShow && (
              <Button variant="outline" size="sm" disabled={isDownloading} onClick={() => downloadPdf(id)}>
                <DownloadIcon className="h-4 w-4" />
                <span className="hidden lg:inline">{t('download_pdf')}</span>
              </Button>
            )}
            {canDelete && (
              <Button variant="destructive" size="sm" onClick={() => setDeleteId(id)}>
                <TrashIcon className="h-4 w-4" />
                <span className="hidden lg:inline">{t('delete')}</span>
              </Button>
            )}
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">{t('personal_information')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              <div className="space-y-1">
                <p className="text-muted-foreground flex items-center gap-1 text-sm">
                  <UserIcon className="h-3.5 w-3.5" />
                  {t('full_name')}
                </p>
                <p className="font-medium">{`${application.firstname} ${application.lastname}`}</p>
              </div>

              <div className="space-y-1">
                <p className="text-muted-foreground flex items-center gap-1 text-sm">
                  <MailIcon className="h-3.5 w-3.5" />
                  {t('email')}
                </p>
                <p className="text-sm">{application.email}</p>
              </div>

              <div className="space-y-1">
                <p className="text-muted-foreground flex items-center gap-1 text-sm">
                  <PhoneIcon className="h-3.5 w-3.5" />
                  {t('phone')}
                </p>
                <p className="text-sm">{application.phone}</p>
              </div>

              <div className="space-y-1">
                <p className="text-muted-foreground flex items-center gap-1 text-sm">
                  <CalendarIcon className="h-3.5 w-3.5" />
                  {t('birth_date')}
                </p>
                <p className="text-sm">{formatDate(application.birthDate, locale)}</p>
              </div>

              <div className="space-y-1">
                <p className="text-muted-foreground flex items-center gap-1 text-sm">
                  <MapPinIcon className="h-3.5 w-3.5" />
                  {t('birth_place')}
                </p>
                <p className="text-sm">{application.birthPlace}</p>
              </div>

              <div className="space-y-1">
                <p className="text-muted-foreground flex items-center gap-1 text-sm">
                  <UserIcon className="h-3.5 w-3.5" />
                  {t('gender')}
                </p>
                <p className="text-sm">{t(application.gender)}</p>
              </div>

              <div className="space-y-1">
                <p className="text-muted-foreground flex items-center gap-1 text-sm">
                  <UserIcon className="h-3.5 w-3.5" />
                  {t('marital_status')}
                </p>
                <p className="text-sm">{t(`marital_status_${application.maritalStatus}`)}</p>
              </div>

              {application.address && (
                <div className="space-y-1 md:col-span-2">
                  <p className="text-muted-foreground flex items-center gap-1 text-sm">
                    <MapPinIcon className="h-3.5 w-3.5" />
                    {t('address')}
                  </p>
                  <p className="text-sm">{application.address}</p>
                </div>
              )}
            </div>

            <Separator className="my-6" />

            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              <div className="space-y-1">
                <p className="text-muted-foreground flex items-center gap-1 text-sm">
                  <CarIcon className="h-3.5 w-3.5" />
                  {t('driver_license')}
                </p>
                <Badge variant={application.driverLicense ? 'success' : 'secondary'}>
                  {application.driverLicense ? t('yes') : t('no')}
                </Badge>
                {application.driverLicense && application.driverLicenseClass && (
                  <p className="text-muted-foreground text-xs">{application.driverLicenseClass}</p>
                )}
              </div>

              <div className="space-y-1">
                <p className="text-muted-foreground flex items-center gap-1 text-sm">
                  <HeartPulseIcon className="h-3.5 w-3.5" />
                  {t('health_problems')}
                </p>
                <Badge variant={application.healthProblems ? 'destructive' : 'success'}>
                  {application.healthProblems ? t('yes') : t('no')}
                </Badge>
                {application.healthProblems && application.healthProblemsDescription && (
                  <p className="text-muted-foreground text-xs">{application.healthProblemsDescription}</p>
                )}
              </div>

              <div className="space-y-1">
                <p className="text-muted-foreground flex items-center gap-1 text-sm">
                  <PlaneIcon className="h-3.5 w-3.5" />
                  {t('travel_restrictions')}
                </p>
                <Badge variant={application.travelRestrictions ? 'destructive' : 'success'}>
                  {application.travelRestrictions ? t('yes') : t('no')}
                </Badge>
                {application.travelRestrictions && application.travelRestrictionsDescription && (
                  <p className="text-muted-foreground text-xs">{application.travelRestrictionsDescription}</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">{t('application_information')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {application.position && (
                <div className="space-y-1">
                  <p className="text-muted-foreground flex items-center gap-1 text-sm">
                    <BriefcaseIcon className="h-3.5 w-3.5" />
                    {t('position')}
                  </p>
                  <p className="font-medium">{application.position}</p>
                </div>
              )}

              {application.availableFrom && (
                <div className="space-y-1">
                  <p className="text-muted-foreground flex items-center gap-1 text-sm">
                    <CalendarIcon className="h-3.5 w-3.5" />
                    {t('available_from')}
                  </p>
                  <p className="text-sm">{formatDate(application.availableFrom, locale)}</p>
                </div>
              )}

              <div className="space-y-1">
                <p className="text-muted-foreground flex items-center gap-1 text-sm">
                  <BookmarkCheckIcon className="h-3.5 w-3.5" />
                  {t('read_status')}
                </p>
                {application.readAt ? (
                  <Badge variant="success">{t('read')}</Badge>
                ) : (
                  <Badge variant="secondary">{t('unread')}</Badge>
                )}
              </div>

              <div className="space-y-1">
                <p className="text-muted-foreground flex items-center gap-1 text-sm">
                  <CalendarIcon className="h-3.5 w-3.5" />
                  {t('created_at')}
                </p>
                <p className="text-sm">{formatDateTime(application.createdAt, locale)}</p>
              </div>

              {application.readAt && (
                <div className="space-y-1">
                  <p className="text-muted-foreground flex items-center gap-1 text-sm">
                    <CalendarIcon className="h-3.5 w-3.5" />
                    {t('read_at')}
                  </p>
                  <p className="text-sm">{formatDateTime(application.readAt, locale)}</p>
                </div>
              )}

              {application.firstReadUser && (
                <div className="space-y-1">
                  <p className="text-muted-foreground flex items-center gap-1 text-sm">
                    <UserIcon className="h-3.5 w-3.5" />
                    {t('first_read_user')}
                  </p>
                  <Link href={`/users/${application.firstReadUser.id}`} className="flex items-center gap-2">
                    <UserAvatar user={application.firstReadUser} size="sm" />
                    <div className="flex flex-col">
                      <span className="text-sm">{application.firstReadUser.fullName}</span>
                      <span className="text-muted-foreground text-xs">{application.firstReadUser.email}</span>
                    </div>
                  </Link>
                </div>
              )}
            </div>

            {application.additionalInformation && (
              <>
                <Separator className="my-6" />
                <div className="space-y-2">
                  <p className="text-muted-foreground text-sm font-medium">{t('additional_information')}</p>
                  <div className="bg-muted rounded-md p-4 text-sm">
                    {application.additionalInformation.split('\n').map((line, i, arr) => (
                      <span key={i}>
                        {line}
                        {i < arr.length - 1 && <br />}
                      </span>
                    ))}
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {!!(
          application.education?.length ||
          application.foreignLanguages?.length ||
          application.workExperience?.length ||
          application.skills?.length ||
          application.certifications?.length ||
          application.references?.length
        ) && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">{t('qualifications')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {application.education && application.education.length > 0 && (
                <EducationTable items={application.education} t={t} locale={locale} />
              )}
              {application.foreignLanguages && application.foreignLanguages.length > 0 && (
                <ForeignLanguagesTable items={application.foreignLanguages} t={t} />
              )}
              {application.workExperience && application.workExperience.length > 0 && (
                <WorkExperienceTable items={application.workExperience} t={t} locale={locale} />
              )}
              {application.skills && application.skills.length > 0 && <SkillsTable items={application.skills} t={t} />}
              {application.certifications && application.certifications.length > 0 && (
                <CertificationsTable items={application.certifications} t={t} locale={locale} />
              )}
              {application.references && application.references.length > 0 && (
                <ReferencesTable items={application.references} t={t} />
              )}
            </CardContent>
          </Card>
        )}

        {application.photo && <ContentDetailPicture picture={application.photo} title="photo" />}
        {application.files && application.files.length > 0 && <ContentDetailFiles files={application.files} />}
      </div>

      {canDelete && (
        <DeleteConfirmationDialog
          open={!!deleteId}
          onCancel={() => setDeleteId(null)}
          onConfirm={confirmDelete}
          isDeleting={isDeleting}
        />
      )}
    </>
  )
}

export default Detail
